import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import clsx from 'clsx';
import TooltipPro from 'epn-ui/umd/dist/components/TooltipPro';
import useI18n from '@i18n';
import sendGaEvent from 'src/utils/metrics/ga';
import {
    ITransactionOrderOption,
    ITransactionsTableData,
} from '@pages/statistics/transactions/interfaces';
import { tableColumns } from '@pages/statistics/transactions/config';

const colors = {
    pending: 'text-sky-500',
    completed: 'text-green-500',
    waiting: 'text-yellow-400',
    rejected: 'text-red-400',
};

const statusesLabels = {
    pending: 'Pending',
    completed: 'Completed',
    waiting: 'Waiting',
    rejected: 'Rejected',
};

const advertFormLabels = {
    short_link: 'Link',
    article: 'Article',
};

const tdClasses = 'py-8 pl-8 text-right whitespace-nowrap text-p3 sm:text-p2';
const tdClassesCreative = 'py-8 pl-8 text-right text-p3 sm:text-p2';

// Для смены направления сортировки
const orderConfig = {
    asc: 'desc',
    desc: 'asc',
};

const iconConfig = {
    asc: faCaretUp,
    desc: faCaretDown,
};

interface ITransactionsTable {
    data: ITransactionsTableData[];
    columns: typeof tableColumns;
    onSort: (value: ITransactionOrderOption) => void;
    orderBy: ITransactionOrderOption;
}

const Table: React.FC<ITransactionsTable> = ({ columns, data, onSort, orderBy }) => {
    const { t } = useI18n('Transactions');

    return (
        <div className="max-w-full overflow-auto">
            <table className="w-full">
                <thead>
                    <tr>
                        {columns.map(({ key, value, sortable, tooltip }) => {
                            const isOrderedBy = orderBy.order === value;

                            // Выставляем направление по умолчанию. Для полей без сортировки будет 'asc'
                            const orderDirection = (isOrderedBy && orderBy.orderDirection) || 'asc';

                            const iconClass = clsx({
                                'text-blueGray-600': isOrderedBy,
                                'text-blueGray-300': !isOrderedBy,
                                'ml-2': true,
                            });

                            const params = {
                                order: value,
                                orderDirection: orderConfig[orderDirection],
                            };

                            return (
                                <th
                                    className="py-4 pl-4 font-normal text-right text-blueGray-500 whitespace-nowrap"
                                    key={key}
                                >
                                    {/* Добавляем callback сортировки только тем колонкам, которым она нужна */}
                                    {sortable ? (
                                        <button type="button" onClick={() => onSort(params)}>
                                            {t(key)}
                                        </button>
                                    ) : (
                                        <span>{t(key)}</span>
                                    )}
                                    {tooltip && (
                                        <TooltipPro placement="top" title={t(tooltip)}>
                                            <FontAwesomeIcon
                                                icon={faInfoCircle}
                                                className="ml-2 cursor-pointer text-blueGray-500 text-[14px]"
                                            />
                                        </TooltipPro>
                                    )}
                                    {/* Если есть возможность сортировки, то рисуем иконку */}
                                    {sortable && (
                                        <FontAwesomeIcon
                                            className={iconClass}
                                            icon={iconConfig[orderDirection]}
                                        />
                                    )}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody className="text-blueGray-800">
                    {data.map(item => {
                        const productLinkExist =
                            typeof item.productLink === 'string' && item.productLink.length > 0;

                        const advertFormText = () => {
                            if (item.advertForm === null) return '';
                            if (advertFormLabels[item.advertForm]) {
                                return t(advertFormLabels[item.advertForm]);
                            }

                            return item.advertForm;
                        };

                        return (
                            <tr
                                key={item.id}
                                className="max-h-20 odd:bg-blueGray-100 hover:bg-green-200/50"
                            >
                                <td className="py-8 text-right text-p3 sm:text-p2">
                                    <p className="w-24">{item.date}</p>
                                </td>
                                <td className="flex flex-col items-end py-8 pl-8 text-p3 sm:text-p2">
                                    {item.offerName && (
                                        <TooltipPro title={item.offerName}>
                                            <p className="overflow-hidden text-right max-w-[134px] overflow-ellipsis whitespace-nowrap text-blueGray-300">
                                                {item.offerName}
                                            </p>
                                        </TooltipPro>
                                    )}
                                    <p className="overflow-auto text-right whitespace-normal max-w-[134px]">
                                        {item.orderNumber}
                                    </p>
                                </td>
                                <td className={tdClasses}>
                                    <div className={`${colors[item.status] || ''}`}>
                                        {t(statusesLabels[item.status])}
                                    </div>
                                    {/* TODO: вынести это из таблицы, после задачи о переиспользуемой таблице */}
                                    <div className="text-blueGray-300">
                                        {item.transactionTime === '0000-00-00 00:00:00'
                                            ? null
                                            : item.transactionTime}
                                    </div>
                                </td>
                                <td
                                    className={`${tdClassesCreative} max-w-xs break-all whitespace-normal`}
                                >
                                    <span className="block overflow-auto max-h-40">
                                        {item.creativeName}
                                    </span>
                                </td>
                                <td className={tdClasses}>
                                    <span>{advertFormText()}</span>
                                </td>
                                <td
                                    className={`${tdClassesCreative} max-w-xs min-w-20 break-all whitespace-normal`}
                                >
                                    <span className="block overflow-auto max-h-20">{item.sub}</span>
                                </td>
                                <td className={tdClasses}>{item.revenue}</td>
                                <td className={tdClasses}>{item.commissionUser}</td>
                                <td className="block py-4 pl-4 text-right">
                                    {productLinkExist && (
                                        <a
                                            href={item.productLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block overflow-auto text-p3 sm:text-p2 hover:underline max-h-24"
                                            onClick={() => {
                                                sendGaEvent({
                                                    eventCategory: 'transactions',
                                                    eventAction: 'view_product',
                                                });
                                            }}
                                        >
                                            {item.product}
                                        </a>
                                    )}

                                    {!productLinkExist && (
                                        <span className="text-p3 sm:text-p2">{item.product}</span>
                                    )}
                                </td>
                                <td className={`${tdClasses} pl-4 truncate max-w-20 xl:max-w-none`}>
                                    {item.clickId}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default Table;
